import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ITooltip {
    content: string;
    children: ReactNode;
    wide?: boolean;
}

export const Tooltip = (props: ITooltip) => {
    const { content, children, wide = false } = props;

    const triggerRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        setCoords({
            top: rect.top - 8,
            left: rect.left + rect.width / 2,
        });
    }, []);

    const handleMouseEnter = () => {
        updatePosition();
        setVisible(true);
    };

    const handleMouseLeave = () => {
        setVisible(false);
    };

    useEffect(() => {
        if (!visible) return;

        const handleUpdate = () => updatePosition();
        window.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);

        return () => {
            window.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
        };
    }, [visible, updatePosition]);

    return (
        <div
            className="tooltip"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="tooltip__trigger" ref={triggerRef}>
                {children}
            </div>
            {visible && createPortal(
                <div
                    className={`tooltip__content ${wide ? 'tooltip__content--wide' : ''}`}
                    style={{ top: coords.top, left: coords.left }}
                >
                    {content}
                </div>,
                document.body
            )}
        </div>
    );
};